/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/RequirementDetailsView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ReactComponent as LocationIcon } from "../../assets/icons/Location_on.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/Calendar_Today.svg";
import { ReactComponent as RequirementIcon } from "../../assets/icons/Requirement.svg";
import { ReactComponent as CategoryIcon } from "../../assets/icons/ProposalsListModal/RequirementCategoryIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";
import ProposalCreationView from "../ProposalCreationView/index.jsx";
import { IconButton, SvgIcon } from "@mui/material";
import dayjs from "dayjs";
import styles from "./index.module.css";
import { getUser } from "../../logic/user/index.js";
import {
  getProposals,
  hasCompanySubmittedProposal,
} from "../../logic/proposal/index.js";
import AssetImage from "../AssetImage";

const RequirementDetailsView = ({
  requirement,
  currentUser,
  onBack
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createProposalModalOpen, setCreateProposalModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProposalStatus = async () => {
    try {
      const proposalsData = await getProposals(navigate);
      setProposals(proposalsData);

      if (user && proposalsData && requirement) {
        const alreadySubmitted = hasCompanySubmittedProposal(
          proposalsData,
          requirement._id,
          user.RFC
        );
        setHasAlreadySubmitted(alreadySubmitted);
      }
    } catch (error) {
      logger.error("Error refreshing proposal status:", { err: sanitizeError(error) });
    }
  };

  const handleProposalCreated = () => {
    setCreateProposalModalOpen(false);
    refreshProposalStatus();
  };

  const handleCloseProposalModal = () => {
    setCreateProposalModalOpen(false);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const userData = await getUser(
          localStorage.getItem("userId"),
          navigate
        );
        setUser(userData);
        const proposalsData = await getProposals(navigate);
        setProposals(proposalsData);

        if (userData && proposalsData && requirement) {
          const alreadySubmitted = hasCompanySubmittedProposal(
            proposalsData,
            requirement._id,
            userData.RFC
          );
          setHasAlreadySubmitted(alreadySubmitted);
        } else {
          setHasAlreadySubmitted(false);
        }
      } catch (error) {
        logger.error("Error fetching user data:", { err: sanitizeError(error) });
        setHasAlreadySubmitted(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, [requirement, navigate]);

  return (
    createProposalModalOpen ? (
      <ProposalCreationView 
        onBack={handleCloseProposalModal}
        requirement={requirement}
        onProposalCreated={handleProposalCreated}
      />
    ) : (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.backNavigation}>
            <IconButton
              onClick={onBack}
            >
              <ChevronLeftIcon styles={{}} />
            </IconButton>
            <h2 className={styles.mainTitle}>
              {requirement.titulo}
            </h2>
          </div>
          <div className={styles.statusItem}>
            <SvgIcon
              sx={{
                fontSize: "14px",
                color: requirement.activo ? "#00E864" : "red",
              }}
              component={RequirementIcon}
            />
            <label className={styles.subtitle}>
              {requirement.activo
                ? t("components.requirementDisplayModal.activeRequirement")
                : t("components.requirementDisplayModal.inactiveRequirement")}
            </label>
          </div>
          <div className={styles.tagsContainer}>
            <div className={styles.categoryTag}>
              <CategoryIcon />
              <span>{requirement.nombreCategoria}</span>
            </div>
            <div className={styles.categoryTag}>
              <CategoryIcon />
              <span>{requirement.nombreSubcategoria}</span>
            </div>
          </div>
        </div>
        <div className={styles.descriptionContainer}>
          <label className={styles.subtitle}>
            {t("components.requirementDisplayModal.description")}
          </label>
          <span>{requirement.descripcion}</span>
        </div>
        <div className={styles.locationContainer}>
          <label className={styles.subtitle}>
            {t("components.requirementDisplayModal.location")}
          </label>
          <div className={styles.locationItem}>
            <SvgIcon sx={{ fontSize: "16px" }} component={LocationIcon} />
            <span>{requirement.ubicacion}</span>
          </div>
        </div>
        <div className={styles.dueDateContainer}>
          <label className={styles.subtitle}>
            {t("components.requirementDisplayModal.dueDate")}
          </label>
          <div className={styles.dueDateItem}>
            <SvgIcon sx={{ fontSize: "16px" }} component={CalendarIcon} />
            <span>{dayjs(requirement.fechaLimite).utc().format("LLL")}</span>
          </div>
        </div>
        <div className={styles.dueDateContainer}>
          <label className={styles.subtitle}>
            {t("components.requirementDisplayModal.minimumExperience")}
          </label>
          <span>
            {requirement.experienciaMinima
              ? requirement.experienciaMinima + " " + t("components.requirementDisplayModal.years")
              : "N/A"
            }
          </span>
        </div>
        <div className={styles.certificationsContainer}>
          {requirement.certificaciones?.length > 0 && (
            <div className={styles.certificationHeader}>
              <label
                style={{ width: "60%" }}
                className={styles.subtitle}
              >
                {t("components.requirementDisplayModal.certifications")}
              </label>
            </div>
          )}
          {requirement.certificaciones?.length > 0 &&
            requirement.certificaciones.map((certification, index) => (
              <div key={index} className={styles.certificationItem}>
                <span style={{ width: "60%" }}>{certification.nombre}</span>
              </div>
            ))}
        </div>
        {requirement.urlImagen && (
          <div className={styles.imageContainer}>
            <AssetImage
              className={styles.image}
              src={requirement.urlImagen}
              alt={t("common.requirementImageAlt")}
            />
          </div>
        )}

        <div className={styles.buttonsContainer}>
          <button onClick={onBack} className={styles.cancelButton}>
            {t("components.requirementDisplayModal.cancel")}
          </button>
          <button
            className={styles.createButton}
            disabled={
              requirement.isFromUser ||
              hasAlreadySubmitted ||
              isLoading ||
              currentUser.tipoDeUsuario === "4"
            }
            onClick={() => {
              setCreateProposalModalOpen(true);
            }}
          >
            {t("components.requirementDisplayModal.createProposal")}
          </button>
        </div>
      </div>
    )
  );
};

export default RequirementDetailsView;